import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getPersonIdFromToken, useAuth } from '@ibk/shared-auth';
import { selectAccessToken, selectSelectedCompanyPartyId, useAppSelector } from '@ibk/shared-config';
import { useFeatureFlag } from '@ibk/shared-feature-flags';
import { useTranslation } from '@ibk/shared-translation';
import { FeatureFlagUseCase } from '@ibk/shared-types';
import { Button, Icon, Input, Loading } from '@ibk/shared-ui';
import { useSendAssistantChatMutation } from '../../services/assistantChatApi';
import styles from './AssistantChat.module.css';
import { assistantErrorKey, buildAssistantContext, MAX_MESSAGE_LENGTH } from './AssistantChat.utils';
function Sparkles() {
    return (_jsxs("svg", { viewBox: '0 0 24 24', width: '24', height: '24', fill: 'none', stroke: 'currentColor', strokeWidth: '2', strokeLinecap: 'round', strokeLinejoin: 'round', "aria-hidden": 'true', children: [_jsx("path", { d: 'm12 3 1.9 5.8a2 2 0 0 0 1.3 1.3L21 12l-5.8 1.9a2 2 0 0 0-1.3 1.3L12 21l-1.9-5.8a2 2 0 0 0-1.3-1.3L3 12l5.8-1.9a2 2 0 0 0 1.3-1.3L12 3Z' }), _jsx("path", { d: 'M5 3v4M3 5h4M19 17v4M17 19h4' })] }));
}
function AssistantConversation() {
    const { t } = useTranslation();
    const [open, setOpen] = useState(false);
    const [minimized, setMinimized] = useState(false);
    const [input, setInput] = useState('');
    const [messages, setMessages] = useState([]);
    const [error, setError] = useState(null);
    const [authorizationDenied, setAuthorizationDenied] = useState(false);
    const [pending, setPending] = useState(false);
    const [sendChat, { reset }] = useSendAssistantChatMutation();
    const resetRef = useRef(reset);
    const requestRef = useRef(null);
    const mountedRef = useRef(true);
    const contextRef = useRef([]);
    const launcherRef = useRef(null);
    const panelRef = useRef(null);
    const messageIdRef = useRef(0);
    const scrollRef = useRef(null);
    const restoreLauncherFocusRef = useRef(false);
    useEffect(() => {
        resetRef.current = reset;
    }, [reset]);
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            requestRef.current?.abort();
            resetRef.current();
        };
    }, []);
    useEffect(() => {
        if (open && !minimized)
            panelRef.current?.querySelector('input')?.focus();
        if (!open && restoreLauncherFocusRef.current) {
            launcherRef.current?.focus();
            restoreLauncherFocusRef.current = false;
        }
    }, [open, minimized]);
    useEffect(() => {
        if (scrollRef.current)
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, pending, error, open, minimized]);
    useEffect(() => {
        const panel = panelRef.current;
        if (!open || !panel)
            return;
        function handleKeyDown(event) {
            if (event.key === 'Escape') {
                event.stopPropagation();
                restoreLauncherFocusRef.current = true;
                setOpen(false);
            }
        }
        panel.addEventListener('keydown', handleKeyDown);
        return () => panel.removeEventListener('keydown', handleKeyDown);
    }, [open]);
    function close() {
        restoreLauncherFocusRef.current = true;
        setOpen(false);
    }
    async function submit(event) {
        event.preventDefault();
        const content = input.trim();
        if (!content || content.length > MAX_MESSAGE_LENGTH || requestRef.current || authorizationDenied)
            return;
        const context = buildAssistantContext(contextRef.current, content);
        const userMessage = { id: ++messageIdRef.current, role: 'user', content };
        setMessages(previous => [...previous, userMessage]);
        setInput('');
        setError(null);
        setPending(true);
        const request = sendChat({ messages: context });
        requestRef.current = request;
        try {
            const response = await request.unwrap();
            if (mountedRef.current) {
                contextRef.current = [...context, response.message];
                const assistantMessage = { ...response.message, id: ++messageIdRef.current };
                setMessages(previous => [...previous, assistantMessage]);
            }
        }
        catch (error_) {
            if (mountedRef.current) {
                const errorKey = assistantErrorKey(error_);
                setError(errorKey);
                setAuthorizationDenied(errorKey === 'assistant.error.authorization');
                setInput(content);
            }
        }
        finally {
            requestRef.current = null;
            if (mountedRef.current)
                setPending(false);
        }
    }
    return createPortal(_jsxs(_Fragment, { children: [_jsx("button", { ref: launcherRef, type: 'button', className: styles.launcher, hidden: open, onClick: () => {
                    setOpen(true);
                    setMinimized(false);
                }, "aria-label": t('assistant.open'), "aria-expanded": open, "aria-controls": 'assistant-chat-panel', "data-testid": 'assistant-chat-launcher', children: _jsx(Sparkles, {}) }), open && (_jsxs("dialog", { ref: panelRef, id: 'assistant-chat-panel', open: true, "aria-modal": 'false', "aria-labelledby": 'assistant-chat-title', className: `${styles.panel} ${minimized ? styles.minimized : ''}`, "data-testid": 'assistant-chat-panel', children: [_jsxs("header", { className: styles.header, children: [_jsx("span", { className: styles.assistantIcon, children: _jsx(Sparkles, {}) }), _jsx("h2", { id: 'assistant-chat-title', children: t('assistant.title') }), _jsx("button", { type: 'button', className: styles.control, onClick: () => setMinimized(value => !value), "aria-label": t(minimized ? 'assistant.restore' : 'assistant.minimize'), "aria-expanded": !minimized, "data-testid": 'assistant-chat-minimize', children: _jsx("span", { "aria-hidden": 'true', children: "\u2212" }) }), _jsx("button", { type: 'button', className: styles.control, onClick: close, "aria-label": t('assistant.close'), children: _jsx(Icon, { name: 'close', "aria-hidden": 'true' }) })] }), !minimized && (_jsxs(_Fragment, { children: [_jsxs("div", { ref: scrollRef, className: styles.messages, role: 'log', "aria-live": 'polite', "aria-relevant": 'additions text', "aria-label": t('assistant.conversation'), "data-dd-privacy": 'mask', children: [_jsx("p", { className: styles.greeting, children: t('assistant.greeting') }), messages.map(message => (_jsxs("div", { className: `${styles.message} ${message.role === 'user' ? styles.userMessage : styles.assistantMessage}`, children: [_jsx("span", { className: styles.role, children: t(message.role === 'user' ? 'assistant.you' : 'assistant.title') }), _jsx("p", { children: message.content })] }, message.id))), pending && (_jsx("output", { children: _jsx(Loading, { size: 'small', message: t('assistant.waiting') }) }))] }), error && (_jsx("p", { role: 'alert', className: styles.error, children: t(error) })), _jsxs("form", { className: styles.composer, onSubmit: submit, "aria-label": t('assistant.message'), children: [_jsx(Input, { id: 'assistant-chat-input', dataTestId: 'assistant-chat-input', size: 'small', label: t('assistant.message'), placeholder: t('assistant.placeholder'), value: input, maxLength: MAX_MESSAGE_LENGTH, onChange: event => setInput(event.target.value), disabled: pending || authorizationDenied, "data-dd-privacy": 'mask' }), _jsx(Button, { type: 'submit', variant: 'primary', size: 'medium', dataTestId: 'assistant-chat-send', disabled: !input.trim() || pending || authorizationDenied, children: t('assistant.send') })] })] }))] }))] }), document.body);
}
export function AssistantChat() {
    const { isAuthenticated } = useAuth();
    const accessToken = useAppSelector(selectAccessToken);
    const selectedPartyId = useAppSelector(selectSelectedCompanyPartyId);
    const personId = accessToken ? getPersonIdFromToken(accessToken) : null;
    const { isFeatureFlagWhiteListed, isLoading, error } = useFeatureFlag(personId ?? undefined);
    if (!isAuthenticated ||
        !personId ||
        !selectedPartyId ||
        isLoading ||
        error ||
        !isFeatureFlagWhiteListed(FeatureFlagUseCase.ERP_SETTINGS))
        return null;
    return _jsx(AssistantConversation, {}, `${personId}:${selectedPartyId}`);
}
